import {
  Autocomplete,
  Box,
  CircularProgress,
  FilledInputProps,
  FormControl,
  FormLabel,
  IconButton,
  InputBaseComponentProps,
  InputProps as InputPropsMui,
  OutlinedInputProps,
  TextField,
} from '@mui/material';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import CloseIcon from '~/assets/images/icons/close.svg';

type TOption = {
  label?: string;
  customLabel?: React.ReactNode | string;
  value?: string;
  defaultOptionFullText?: string;
  place_id?: any;
};

export interface AsyncSelectProps {
  transformOptions?: (data: any) => Array<any>;
  fetchOptions?: (searchText: string) => Promise<any>;
  onChange: (event: React.SyntheticEvent, value: TOption) => void;
  defaultOptions?: TOption[];
  id?: string;
  legend?: string;
  helperText?: string;
  error?: boolean;
  value?: any;
  inputProps?: InputBaseComponentProps;
  onBlur?: (e: any) => void;
  InputProps?:
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | Partial<InputPropsMui>;
  uniqueValue?: boolean;
  onClear?: () => void;
  placeholder?: string;
  debounceTime?: number;
  minSearchLength?: number;
}

export const AsyncSelect: React.FC<AsyncSelectProps> = ({
  id,
  legend,
  value,
  helperText,
  error,
  inputProps,
  onChange,
  transformOptions,
  fetchOptions,
  defaultOptions = [],
  onBlur,
  InputProps,
  uniqueValue = false,
  onClear,
  placeholder = '',
  debounceTime = 300,
  minSearchLength = 0,
  ...rest
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value);

  const [debouncedSearchText] = useDebounce(inputValue, debounceTime);

  const [options, setOptions] = useState<TOption[]>(defaultOptions);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }

    if (debouncedSearchText.trim() === '') {
      setOptions(defaultOptions);
      return;
    }

    // Check if search text meets minimum length requirement
    if (debouncedSearchText.trim().length < minSearchLength) {
      setOptions(defaultOptions);
      return;
    }

    let isSubscribed = true;

    const request = async () => {
      setLoading(true);
      setOptions([]);
      try {
        const data = await fetchOptions(debouncedSearchText);

        if (isSubscribed) {
          const newOptions = defaultOptions
            .filter((e) =>
              e.defaultOptionFullText
                ?.toLowerCase()
                .includes(debouncedSearchText?.toLowerCase()),
            )
            .concat(transformOptions(data));
          if (uniqueValue) {
            const uniqOptions = uniqBy(newOptions, (option) => option?.value);
            setOptions(uniqOptions);
          } else setOptions(newOptions);
        }
      } catch (e) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    request();

    // eslint-disable-next-line consistent-return
    return () => {
      isSubscribed = false;
    };
  }, [debouncedSearchText, open, minSearchLength]);

  const renderEndAdornment = () => {
    if (loading)
      return (
        <CircularProgress
          color='inherit'
          size={18}
          style={{ marginRight: 12 }}
        />
      );
    if (onClear && inputValue)
      return (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setInputValue('');
            onClear();
          }}
          size='large'
          sx={{
            marginRight: 0,
          }}
        >
          <img alt='clear-icon' src={CloseIcon} width={11} height={11} />
        </IconButton>
      );
    return null;
  };

  return (
    <FormControl fullWidth>
      {legend && (
        <FormLabel>{typeof legend === 'string' ? legend : legend}</FormLabel>
      )}
      <Autocomplete
        onBlur={() => onBlur(inputValue)}
        disablePortal
        id={id}
        data-testid={`txt-${id}`}
        options={options}
        open={open}
        value={value}
        inputValue={inputValue}
        forcePopupIcon={false}
        disableClearable
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onInputChange={(_, val, reason) => {
          if (reason === 'input') setInputValue(val);
        }}
        onChange={(_, val) => {
          if (val?.inputValue) setInputValue(val?.inputValue);
          else if (val?.place_id) setInputValue(val?.value);
          else setInputValue(val?.address?.address_1);
          onChange(_, val);
        }}
        getOptionLabel={(option: TOption) =>
          typeof option === 'string' ? option : option?.label || ''
        }
        loadingText='loading...'
        noOptionsText='No result'
        filterOptions={(o) => o}
        isOptionEqualToValue={(option: TOption, _value) =>
          option?.place_id
            ? option?.defaultOptionFullText === _value
            : option?.value === _value
        }
        renderInput={(params) => (
          <TextField
            {...params}
            helperText={helperText}
            error={error}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              ...InputProps,
              inputProps: {
                ...inputProps,
                ...params.inputProps,
                'aria-label':
                  typeof legend === 'string' ? legend : 'input-label',
                'autoComplete': 'new-password',
                'data-testid': `txt-${id}`,
              },
              endAdornment: renderEndAdornment(),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component='li'
            data-testid={`ddl-${option.value || ''}`}
            data-value={`${option.value}-async-select`}
            sx={{
              display: 'block !important',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            {...props}
            key={option.value}
          >
            {option.customLabel ? option.customLabel : option?.label || ''}
          </Box>
        )}
        {...rest}
      />
    </FormControl>
  );
};
